import React, { useCallback, useEffect, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ohif/ui-next';

import { ICON_STROKE_WIDTH } from '../../constants';
import appointmentReminderConfigService, {
  type AppointmentReminderConfig,
} from '../../services/appointmentReminderConfigService';

/** Max reminder lead time: 7 days in hours (must match backend). */
export const MAX_APPOINTMENT_REMINDER_HOURS = 168;

interface AppointmentReminderRecord {
  id: number;
  reminderHours: number;
  updatedAt: string;
}

interface AppointmentReminderSectionProps {
  facilityId: number;
}

function sanitizeHoursInput(value: string): string {
  return value.replaceAll(/\D/g, '');
}

function parsePositiveIntegerHours(value: string): number | null {
  if (!value || !/^\d+$/.test(value)) {
    return null;
  }
  const hours = Number(value);
  if (!Number.isInteger(hours) || hours < 1) {
    return null;
  }
  if (hours > MAX_APPOINTMENT_REMINDER_HOURS) {
    return null;
  }
  return hours;
}

function mapApiRecord(row: AppointmentReminderConfig): AppointmentReminderRecord {
  return {
    id: row.id,
    reminderHours: row.reminderHours,
    updatedAt: row.updatedAt || row.createdAt || new Date().toISOString(),
  };
}

function formatReminderHoursLabel(hours: number): string {
  const unit = hours === 1 ? 'hour' : 'hours';
  return `${hours} ${unit}`;
}

function getReminderModalSaveLabel(saving: boolean, isEditing: boolean): string {
  if (saving) {
    return 'Saving…';
  }
  if (isEditing) {
    return 'Save changes';
  }
  return 'Create';
}

function AppointmentReminderTableBody({
  loading,
  record,
  saving,
  onEdit,
  onDeleteRequest,
}: Readonly<{
  loading: boolean;
  record: AppointmentReminderRecord | null;
  saving: boolean;
  onEdit: () => void;
  onDeleteRequest: () => void;
}>) {
  if (loading) {
    return (
      <tr>
        <td
          colSpan={4}
          className="esc-table-empty"
        >
          Loading…
        </td>
      </tr>
    );
  }

  if (!record) {
    return (
      <tr>
        <td
          colSpan={4}
          className="esc-table-empty"
        >
          No appointment reminder configured. Add a record to enable pre-appointment SMS reminders.
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="esc-table-index">1</td>
      <td className="esc-table-name">{formatReminderHoursLabel(record.reminderHours)}</td>
      <td>
        {new Date(record.updatedAt).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}
      </td>
      <td className="esc-table-actions-col">
        <div className="esc-table-actions">
          <button
            type="button"
            className="esc-icon-btn"
            aria-label="Edit appointment reminder config"
            onClick={onEdit}
            disabled={saving}
          >
            <Pencil
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
          </button>
          <button
            type="button"
            className="esc-icon-btn esc-icon-btn--danger"
            aria-label="Delete appointment reminder config"
            onClick={onDeleteRequest}
            disabled={saving}
          >
            <Trash2
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function AppointmentReminderSection({
  facilityId,
}: Readonly<AppointmentReminderSectionProps>) {
  const [record, setRecord] = useState<AppointmentReminderRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hoursInput, setHoursInput] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const loadRecord = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const result = await appointmentReminderConfigService.list(facilityId);
    if (!result.success) {
      setRecord(null);
      setLoadError(result.error || 'Failed to load appointment reminder config');
      setLoading(false);
      return;
    }
    const first = result.data?.[0];
    setRecord(first ? mapApiRecord(first) : null);
    setLoading(false);
  }, [facilityId]);

  useEffect(() => {
    loadRecord();
  }, [loadRecord]);

  const openCreateModal = () => {
    setIsEditing(false);
    setHoursInput('');
    setFieldError(null);
    setModalOpen(true);
  };

  const openEditModal = () => {
    if (!record) {
      return;
    }
    setIsEditing(true);
    setHoursInput(String(record.reminderHours));
    setFieldError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) {
      return;
    }
    setModalOpen(false);
    setFieldError(null);
  };

  const handleHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = sanitizeHoursInput(event.target.value);
    setHoursInput(next);
    if (fieldError) {
      setFieldError(null);
    }
  };

  const handleHoursKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
      event.preventDefault();
    }
  };

  const handleSave = useCallback(async () => {
    const reminderHours = parsePositiveIntegerHours(hoursInput);
    if (reminderHours == null) {
      setFieldError(
        `Enter a whole number of hours between 1 and ${MAX_APPOINTMENT_REMINDER_HOURS} (7 days).`
      );
      return;
    }

    setSaving(true);
    setFieldError(null);

    const result =
      isEditing && record
        ? await appointmentReminderConfigService.update(record.id, { facilityId, reminderHours })
        : await appointmentReminderConfigService.create({ facilityId, reminderHours });

    setSaving(false);

    if (!result.success || !result.data) {
      setFieldError(result.error || 'Failed to save appointment reminder config');
      return;
    }

    setRecord(mapApiRecord(result.data));
    setModalOpen(false);
  }, [facilityId, hoursInput, isEditing, record]);

  const handleDelete = async () => {
    if (!record) {
      return;
    }
    setSaving(true);
    const result = await appointmentReminderConfigService.remove(record.id, facilityId);
    setSaving(false);
    if (!result.success) {
      setLoadError(result.error || 'Failed to delete appointment reminder config');
      setDeleteConfirmOpen(false);
      return;
    }
    setRecord(null);
    setDeleteConfirmOpen(false);
  };

  return (
    <section className="esc-step-card">
      <header className="esc-step-header">
        <div className="esc-step-title-row">
          <h2 className="esc-step-title">Appointment reminder</h2>
          <span className="esc-count-badge">{record ? 1 : 0}</span>
        </div>
        <p className="esc-step-subtitle">
          Send the same appointment confirmation SMS again this many hours before the scheduled
          appointment time. A Service Bus message is scheduled when the appointment is booked or
          rescheduled; if the slot changes, the previous reminder is cancelled automatically.
          Patients still receive an immediate SMS when the appointment is booked or rescheduled. If
          the appointment is booked within this window, no additional reminder is sent.
        </p>
      </header>

      {loadError ? (
        <p
          className="esc-field-error"
          role="alert"
        >
          {loadError}
        </p>
      ) : null}

      <div className="esc-toolbar esc-toolbar--contacts">
        <div className="esc-toolbar-actions">
          <button
            type="button"
            className="esc-btn esc-btn--primary esc-btn--inline"
            onClick={openCreateModal}
            disabled={Boolean(record) || loading || saving}
            title={record ? 'Delete the existing record before creating a new one' : undefined}
          >
            <Plus
              size={16}
              strokeWidth={ICON_STROKE_WIDTH}
            />
            Add appointment reminder
          </button>
        </div>
      </div>

      <div className="esc-table-wrap">
        <table className="esc-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col">Hours before appointment</th>
              <th scope="col">Last updated</th>
              <th
                className="esc-table-actions-col"
                aria-label="Actions"
              />
            </tr>
          </thead>
          <tbody>
            <AppointmentReminderTableBody
              loading={loading}
              record={record}
              saving={saving}
              onEdit={openEditModal}
              onDeleteRequest={() => setDeleteConfirmOpen(true)}
            />
          </tbody>
        </table>
      </div>

      <Dialog
        open={modalOpen}
        onOpenChange={open => !open && closeModal()}
      >
        <DialogContent className="esc-contact-dialog max-w-[480px] bg-white text-[#0a0a0a]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {isEditing ? 'Edit appointment reminder' : 'Add appointment reminder'}
            </DialogTitle>
          </DialogHeader>

          <div className="esc-activate-dialog-body">
            <label
              className="esc-field"
              htmlFor="appointment-reminder-hours"
            >
              <span className="esc-field-label">Hours before appointment</span>
              <input
                id="appointment-reminder-hours"
                className={['esc-input', fieldError ? 'esc-input--error' : '']
                  .filter(Boolean)
                  .join(' ')}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                placeholder="e.g. 2"
                value={hoursInput}
                onChange={handleHoursChange}
                onKeyDown={handleHoursKeyDown}
                aria-invalid={Boolean(fieldError)}
                aria-describedby={
                  fieldError
                    ? 'appointment-reminder-hours-error'
                    : 'appointment-reminder-hours-help'
                }
                disabled={saving}
              />
              <span
                id="appointment-reminder-hours-help"
                className="esc-field-helper"
              >
                Whole numbers from 1 to {MAX_APPOINTMENT_REMINDER_HOURS} (7 days). Decimals,
                negatives, and letters are not allowed.
              </span>
              {fieldError ? (
                <span
                  id="appointment-reminder-hours-error"
                  className="esc-field-error"
                  role="alert"
                >
                  {fieldError}
                </span>
              ) : null}
            </label>
          </div>

          <div className="esc-dialog-actions">
            <button
              type="button"
              className="esc-btn esc-btn--secondary"
              onClick={closeModal}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="esc-btn esc-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {getReminderModalSaveLabel(saving, isEditing)}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={open => !open && !saving && setDeleteConfirmOpen(false)}
      >
        <DialogContent className="esc-contact-dialog max-w-[480px] bg-white text-[#0a0a0a]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Delete appointment reminder</DialogTitle>
          </DialogHeader>

          <div className="esc-activate-dialog-body">
            <p>
              Are you sure you want to delete the appointment reminder of{' '}
              <strong>{record ? formatReminderHoursLabel(record.reminderHours) : ''}</strong> before
              the scheduled time? You can create a new record afterward.
            </p>
          </div>

          <div className="esc-dialog-actions">
            <button
              type="button"
              className="esc-btn esc-btn--secondary"
              onClick={() => setDeleteConfirmOpen(false)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="esc-btn esc-btn--primary study-assignment-delete-confirm-btn"
              onClick={handleDelete}
              disabled={saving}
            >
              {saving ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
